<template>
  <div class="add-connection-panel">
    <div class="header-row">
      <h3>Add Graph Connections</h3>
      <div class="actions">
        <button @click="$emit('cancel')">Cancel</button>
      </div>
    </div>

    <!-- Node Type Selection -->
    <div class="filters-row">
      <div class="filter">
        <label>Node Type</label>
        <div class="node-type-selector">
          <label class="radio-label">
            <input type="radio" value="experiment" v-model="selectedNodeType" />
            <span>Experiments</span>
          </label>
          <label class="radio-label">
            <input type="radio" value="record" v-model="selectedNodeType" />
            <span>Records</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Filters for Experiments -->
    <div class="filters-row" v-if="selectedNodeType === 'experiment'">
      <div class="filter">
        <label>Projects</label>
        <multiselect
          v-model="selectedProjects"
          :options="projects"
          :multiple="true"
          :searchable="true"
          label="name"
          track-by="id"
          placeholder="Select projects"
          class="filter-multiselect"
        />
      </div>

      <div class="filter">
        <label>Locations</label>
        <multiselect
          v-model="selectedLocations"
          :options="locations"
          :multiple="true"
          :searchable="true"
          label="name"
          track-by="id"
          placeholder="Select locations"
          class="filter-multiselect"
        />
      </div>

      <div class="filter actions-inline">
        <button class="btn" :disabled="!canFetchExperiments || loading" @click="fetchExperiments">
          {{ loading ? 'Fetching…' : 'Fetch experiments' }}
        </button>
      </div>
    </div>

    <!-- Filters for Records -->
    <div class="filters-row" v-if="selectedNodeType === 'record'">
      <div class="filter">
        <label>Record Types</label>
        <multiselect
          v-model="selectedRecordTypes"
          :options="recordTypes"
          :multiple="true"
          :searchable="true"
          label="name"
          track-by="id"
          placeholder="Select record types"
          class="filter-multiselect"
        />
      </div>

      <div class="filter actions-inline">
        <button class="btn" :disabled="loading" @click="fetchRecords">
          {{ loading ? 'Fetching…' : 'Fetch records' }}
        </button>
      </div>
    </div>

    <!-- Relation Type Selection -->
    <div class="relation-row" v-if="results.length > 0">
      <div class="filter">
        <label>Relation Type</label>
        <div class="relation-selector">
          <select v-model.number="selectedRelationType" class="relation-select">
            <option :value="null" disabled>Select relation type</option>
            <option v-for="rt in relationTypes" :key="rt.id" :value="rt.id">
              {{ rt.name }}{{ rt.category ? ` (${rt.category})` : '' }}
            </option>
          </select>
          <button type="button" class="create-btn" @click="showCreateRelationDialog = true" title="Create new relation type">
            <i class="bi bi-plus-lg"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Create Relation Type Dialog -->
    <div v-if="showCreateRelationDialog" class="modal-overlay" @click.self="showCreateRelationDialog = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Create New Relation Type</h3>
          <button type="button" class="close-btn" @click="showCreateRelationDialog = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="relation-name">Name</label>
            <input
              type="text"
              id="relation-name"
              v-model="newRelationType.name"
              class="form-control"
              placeholder="e.g., depends_on, relates_to, etc."
            />
          </div>
          <div class="form-group">
            <label for="relation-category">Category (optional)</label>
            <input
              type="text"
              id="relation-category"
              v-model="newRelationType.category"
              class="form-control"
              placeholder="e.g., references, workflow, etc."
            />
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="showCreateRelationDialog = false">Cancel</button>
          <button type="button" class="save-btn" @click="createRelationType" :disabled="creatingRelationType">
            {{ creatingRelationType ? 'Creating...' : 'Create' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div class="results-card" v-if="results.length > 0">
      <table class="results-table">
        <thead>
          <tr>
            <th style="width:70px;">Add</th>
            <th>Name</th>
            <th v-if="selectedNodeType === 'experiment'">Project</th>
            <th v-if="selectedNodeType === 'experiment'">Location</th>
            <th v-if="selectedNodeType === 'record'">Type</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in results" :key="getNodeId(item)">
            <td class="center">
              <input
                type="checkbox"
                :checked="selectedIds.has(getNodeId(item))"
                @change="toggleSelect(getNodeId(item), $event.target.checked)"
              />
            </td>
            <td class="ellipsis" :title="item.name">{{ item.name }}</td>
            
            <!-- Experiment-specific columns -->
            <template v-if="selectedNodeType === 'experiment'">
              <td class="ellipsis" :title="projectName(item.project_id)">
                {{ projectName(item.project_id) }}
              </td>
              <td class="ellipsis" :title="locationName(item.location_id)">
                {{ locationName(item.location_id) }}
              </td>
            </template>
            
            <!-- Record-specific columns -->
            <template v-if="selectedNodeType === 'record'">
              <td class="ellipsis" :title="recordTypeName(item.record_type_id)">
                {{ recordTypeName(item.record_type_id) }}
              </td>
            </template>
          </tr>

          <tr v-if="error">
            <td :colspan="selectedNodeType === 'experiment' ? 5 : 3" class="error center">{{ error }}</td>
          </tr>
        </tbody>
      </table>

      <div class="footer-row">
        <button 
          class="btn" 
          :disabled="selectedIds.size === 0 || !selectedRelationType || linking" 
          @click="createConnections"
        >
          {{ linking ? 'Connecting…' : 'Create connections' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Multiselect from "vue-multiselect";
import { API_ENDPOINTS } from "@/services/config";
import { mapState } from "vuex";
import { useToast } from "vue-toastification";

export default {
  name: "AddGraphConnection",
  components: { Multiselect },
  props: {
    sourceNodeId: { type: Number, required: true },
  },
  data() {
    return {
      selectedNodeType: 'experiment',
      
      // Experiment filters
      projects: [],
      locations: [],
      selectedProjects: [],
      selectedLocations: [],

      // Record filters
      recordTypes: [],
      selectedRecordTypes: [],

      // Relation types
      relationTypes: [],
      selectedRelationType: null,
      showCreateRelationDialog: false,
      newRelationType: {
        name: '',
        category: '',
      },
      creatingRelationType: false,

      results: [],
      selectedIds: new Set(),
      linkedNodeIds: new Set(),

      loading: false,
      linking: false,
      error: "",
    };
  },
  computed: {
    ...mapState(["loggedInUser"]),
    canFetchExperiments() {
      return (
        this.selectedProjects.length > 0 ||
        this.selectedLocations.length > 0
      );
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.fetchFilters();
    this.fetchRelationTypes();
    this.fetchExistingLinkedNodeIds();
  },
  watch: {
    selectedNodeType() {
      this.results = [];
      this.selectedIds.clear();
      this.error = "";
    },
  },
  methods: {
    async fetchFilters() {
      try {
        const [projRes, locRes, rtRes] = await Promise.all([
          axios.get(API_ENDPOINTS.projects, { params: { entra_id: this.loggedInUser?.entra_id } }),
          axios.get(API_ENDPOINTS.locations),
          axios.get(API_ENDPOINTS.recordTypes),
        ]);

        console.log('Graph connection projects response:', projRes.data);
        console.log('Graph connection locations response:', locRes.data);

        this.projects = Array.isArray(projRes.data) ? projRes.data : [];
        this.locations = Array.isArray(locRes.data) ? locRes.data : [];
        this.recordTypes = Array.isArray(rtRes.data) ? rtRes.data : [];
      } catch (e) {
        console.error("Failed to load filter options", e);
      }
    },

    async fetchRelationTypes() {
      try {
        const res = await axios.get(API_ENDPOINTS.graphRelationTypes);
        this.relationTypes = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Failed to load relation types", e);
      }
    },

    projectName(id) {
      const x = this.projects.find(p => +p.id === +id);
      return x ? x.name : "—";
    },
    locationName(id) {
      const x = this.locations.find(l => +l.id === +id);
      return x ? x.name : "—";
    },
    recordTypeName(id) {
      const x = this.recordTypes.find(rt => +rt.id === +id);
      return x ? x.name : "—";
    },
    getNodeId(item) {
      return item?.node_id ?? item?.id ?? null;
    },

    async fetchExistingLinkedNodeIds() {
      try {
        const res = await axios.get(API_ENDPOINTS.graphMap, {
          params: {
            entra_id: this.loggedInUser?.entra_id,
            start_node_id: this.sourceNodeId,
          },
        });

        const edges = Array.isArray(res.data?.edges) ? res.data.edges : [];
        const linkedIds = new Set();

        edges.forEach((edge) => {
          const src = Number(edge.src_node_id);
          const dst = Number(edge.dst_node_id);
          if (src === Number(this.sourceNodeId)) {
            linkedIds.add(dst);
          }
          if (dst === Number(this.sourceNodeId)) {
            linkedIds.add(src);
          }
        });

        this.linkedNodeIds = linkedIds;
      } catch (error) {
        console.warn("Failed to fetch existing graph links", error);
        this.linkedNodeIds = new Set();
      }
    },

    excludeAlreadyLinked(items) {
      const sourceId = Number(this.sourceNodeId);
      return (Array.isArray(items) ? items : []).filter((item) => {
        const nodeId = Number(this.getNodeId(item));
        if (!Number.isFinite(nodeId)) return false;
        if (nodeId === sourceId) return false;
        return !this.linkedNodeIds.has(nodeId);
      });
    },

    async fetchExperiments() {
      this.loading = true;
      this.error = "";
      this.results = [];
      this.selectedIds.clear();

      try {
        await this.fetchExistingLinkedNodeIds();

        const filters = {};
        if (this.selectedProjects.length) filters.project_ids = this.selectedProjects.map(p => p.id);
        if (this.selectedLocations.length) filters.location_ids = this.selectedLocations.map(l => l.id);

        // Page through the results so already-linked filtering sees the full set
        const limit = 100;
        const experiments = [];
        let offset = 0;
        let hasMore = true;
        while (hasMore) {
          const res = await axios.post(API_ENDPOINTS.filteredExperiments, {
            entra_id: this.loggedInUser?.entra_id,
            filters,
            pagination: { offset, limit },
            sort: { by: "name", order: "asc" },
          });
          const list = res.data?.items ?? res.data?.experiments;
          const items = Array.isArray(list) ? list : [];
          experiments.push(...items);
          hasMore = Boolean(res.data?.pagination?.has_more) && items.length > 0;
          offset += limit;
        }
        this.results = this.excludeAlreadyLinked(experiments);
      } catch (e) {
        console.error(e);
        this.error = "Failed to fetch experiments.";
      } finally {
        this.loading = false;
      }
    },

    async fetchRecords() {
      this.loading = true;
      this.error = "";
      this.results = [];
      this.selectedIds.clear();

      try {
        await this.fetchExistingLinkedNodeIds();

        const res = await axios.get(API_ENDPOINTS.allRecords, {
          params: { entra_id: this.loggedInUser?.entra_id },
        });
        
        let records = Array.isArray(res.data) ? res.data : [];
        
        // Filter by selected record types if any
        if (this.selectedRecordTypes.length > 0) {
          const selectedTypeIds = this.selectedRecordTypes.map(rt => rt.id);
          records = records.filter(r => selectedTypeIds.includes(r.record_type_id));
        }

        this.results = this.excludeAlreadyLinked(records);
      } catch (e) {
        console.error(e);
        this.error = "Failed to fetch records.";
      } finally {
        this.loading = false;
      }
    },

    toggleSelect(nodeId, checked) {
      if (!nodeId) return;
      if (checked) this.selectedIds.add(nodeId);
      else this.selectedIds.delete(nodeId);
      this.selectedIds = new Set(this.selectedIds);
    },

    async createRelationType() {
      if (!this.newRelationType.name.trim()) {
        this.toast.error("Relation type name is required.");
        return;
      }

      this.creatingRelationType = true;
      try {
        const body = {
          name: this.newRelationType.name.trim(),
          category: this.newRelationType.category.trim() || null,
        };

        const res = await axios.post(API_ENDPOINTS.graphRelationTypes, body);
        
        // Add the new relation type to the list
        if (res.data && res.data.id) {
          this.relationTypes.push(res.data);
          this.selectedRelationType = res.data.id;
          this.toast.success("Relation type created successfully!");
        }
        
        this.showCreateRelationDialog = false;
        this.newRelationType = { name: '', category: '' };
      } catch (e) {
        console.error(e);
        this.toast.error("Failed to create relation type.");
      } finally {
        this.creatingRelationType = false;
      }
    },

    async createConnections() {
      if (this.selectedIds.size === 0 || !this.selectedRelationType) return;
      
      this.linking = true;
      try {
        const targetNodeIds = Array.from(this.selectedIds).filter((id) => id !== null && id !== undefined);
        if (targetNodeIds.length === 0) {
          this.toast.error("No valid target node IDs selected.");
          return;
        }

        await Promise.all(
          targetNodeIds.map(dstNodeId =>
            axios.post(API_ENDPOINTS.graphEdges, {
              entra_id: this.loggedInUser?.entra_id,
              src_node_id: this.sourceNodeId,
              dst_node_id: dstNodeId,
              relation_type_id: this.selectedRelationType,
            })
          )
        );
        this.toast.success("Connections created.");
        this.$emit("connected");
      } catch (e) {
        console.error(e);
        this.toast.error("Failed to create connections.");
      } finally {
        this.linking = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";
@import "~vue-multiselect/dist/vue-multiselect.min.css";

.add-connection-panel {
  background: $secondary-color;
  border-radius: 8px;
  padding: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
}

.header-row {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;
  h3 { margin: 0; color: $primary-color; }
}
.actions { display: flex; gap: 8px; }

.node-type-selector {
  display: flex;
  gap: 16px;
  padding: 8px 0;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  
  input[type="radio"] {
    cursor: pointer;
  }
}

.filters-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
  gap: 12px;
  margin-bottom: 12px;

  .filter {
    display: grid;
    gap: 6px;
    min-width: 0;
  }

  .actions-inline {
    align-self: end;
    white-space: nowrap;
  }
}

.filter-multiselect {
  width: 100%;
  min-width: 0;
}

::v-deep(.filter-multiselect .multiselect) {
  width: 100%;
  min-width: 0;
}

::v-deep(.filter-multiselect .multiselect__tags) {
  min-width: 0;
  overflow: hidden;
}

::v-deep(.filter-multiselect .multiselect__content-wrapper) {
  min-width: 100%;
  max-width: 100%;
}

::v-deep(.filter-multiselect .multiselect__tags-wrap) {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}

::v-deep(.filter-multiselect .multiselect__tag) {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
}

::v-deep(.filter-multiselect .multiselect__tag span) {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep(.filter-multiselect .multiselect__single),
::v-deep(.filter-multiselect .multiselect__placeholder),
::v-deep(.filter-multiselect .multiselect__input) {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 1100px) {
  .filters-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .filters-row {
    grid-template-columns: minmax(0, 1fr);
  }
}

.relation-row {
  margin-bottom: 12px;
  
  .filter { 
    display: grid; 
    gap: 6px; 
    max-width: 400px;
  }
  
  .relation-selector {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  
  .relation-select {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #e3e3e7;
    border-radius: 6px;
    background: white;
    font-size: 14px;
  }
  
  .create-btn {
    padding: 8px 10px;
    background: $primary-color;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    
    &:hover {
      opacity: 0.9;
    }
  }
}

.btn, .btn-secondary {
  border-radius: 6px; cursor: pointer; border: none; padding: 8px 12px;
}
.btn { background: $primary-color; color: #fff; }
.btn:disabled { opacity: .6; cursor: not-allowed; }
.btn-secondary { background: lighten($primary-color, 35%); color: #222; }

.results-card {
  background: #fff; border: 1px solid #e8e8ec; border-radius: 8px; overflow: hidden;
}

.results-table {
  width: 100%; table-layout: fixed; border-collapse: collapse;
}
.results-table th { background: #fafafa; text-align: left; }
.results-table th, .results-table td {
  border-bottom: 1px solid #eee; padding: 8px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.center { text-align: center; }

.footer-row {
  display: flex; justify-content: flex-end; padding: 10px;
}

.clickable { color: $primary-color; text-decoration: underline; cursor: pointer; }
.muted { color: #7a7a7a; }
.error { color: #d33; }
.ellipsis { text-overflow: ellipsis; overflow: hidden; }

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e8e8ec;
  
  h3 {
    margin: 0;
    color: $primary-color;
    font-size: 18px;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      color: #333;
    }
  }
}

.modal-body {
  padding: 16px;
  
  .form-group {
    margin-bottom: 12px;
    
    label {
      display: block;
      margin-bottom: 6px;
      font-weight: 600;
      color: #333;
      font-size: 14px;
    }
    
    .form-control {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #e3e3e7;
      border-radius: 6px;
      font-size: 14px;
      
      &:focus {
        outline: none;
        border-color: $primary-color;
        box-shadow: 0 0 0 3px rgba(var(--primary-color), 0.1);
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e8e8ec;
  background: #fafafa;
  
  button {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
  }
}

.cancel-btn {
  background: #e8e8ec;
  color: #333;
  
  &:hover {
    background: #dcdce2;
  }
}

.save-btn {
  background: $primary-color;
  color: white;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
